// ========================================
// FIXED MobileReportTable.js - Preserve globalFilters correctly
// ========================================

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardBody, Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import FalconCardHeader from 'components/common/FalconCardHeader';
import CardDropdown from 'components/common/CardDropdown';
import ExportButton from 'components/common/ExportButton';
import ServerSideTableFooter from 'components/common/advance-table/ServerSideTableFooter';
import TableResponsiveCard from 'components/common/TableResponsiveCard';
import ViewComponent from './ViewComponent';
import DeliveryDetailsModal from 'screens/common/tanStackTable/DeliveryDetailsModal';
import DynamicModal from '../DynamicCollapseReport/DynamicModal';
import { getTenantFromToken } from 'utils/routeUtils';
import MobileFilters from './MobileFilters';

const MobileReportTable = ({
  title,
  buttons,
  showHeader = true,
  showFooter = true,
  exportKey,
  handleDownload,
  rowCount,
  isGlobalSearchActive,
  filteredTableData,
  flattenedData,
  cardTittles,
  handleAction,
  errorMsg,
  globalfilters = { search_text: '', start_date: '', end_date: '' }, // Safe default here
  pageSize,
  pageIndex,
  canNextPage,
  handlePageSizeChange,
  handlePageChange,
  // Filter props
  globalSearch,
  dateFilter,
  dropdownFilter,
  dropdownData,
  dropdownOptions,
  selectedValues,
  setSelectedValues,
  handleGlobalSearch,
  handleDateChange,
  customStyles,
  clearAllDates
}) => {
  const tenant = getTenantFromToken();
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showButtonModal, setShowButtonModal] = useState(false);
  const [selectedModalData, setSelectedModalData] = useState(null);

  // ===== PRESERVE ACTUAL GLOBAL FILTERS - DON'T OVERRIDE =====
  const safeGlobalFilters = globalfilters || {
    search_text: '',
    start_date: '',
    end_date: '',
    dateRange: {
      start_date: '',
      end_date: ''
    }
  };

  const renderCollapsibleContent = data => {
    if (!data || typeof data !== 'object') return null;

    return (
      <div className="p-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="mb-3">
            {key === 'title' && (
              <p className="text-uppercase fw-bold mb-2 text-700 fs--1 border-bottom pb-1">
                {value}
              </p>
            )}
            {key === 'data' && (
              <div className="row g-3">
                {Object.entries(value).map(([subKey, subValue], index) => {
                  const text = Array.isArray(subValue) ? subValue[0] : subValue;
                  const color =
                    Array.isArray(subValue) && subValue[1]
                      ? subValue[1]
                      : 'inherit';

                  return (
                    <div key={index} className="col-12 col-sm-6 col-md-4">
                      <div className="d-flex flex-column">
                        <span className="text-500 fw-semi-bold fs--2 text-uppercase mb-1">
                          {subKey}
                        </span>
                        <span className="text-800 fs--1" style={{ color }}>
                          {text || '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const handleMobileAction = (action, rowData) => {
    if (action === 'view') {
      const farmerId = rowData.id;
      setShowViewModal(true);
      setSelectedFarmerId(farmerId);
      setSelectedRow(rowData);
    } else if (action === 'viewData') {
      const modalData = { ...rowData };
      setShowButtonModal(true);
      setSelectedModalData(modalData);
    } else if (action === 'downloadCoords') {
      handleDownloadJSON(rowData);
    } else {
      handleAction(action, rowData);
    }
  };

  const handleDownloadJSON = rowData => {
    try {
      let coords = rowData.coordinates;
      if (!coords) return;

      if (typeof coords === 'string') {
        try {
          coords = JSON.parse(coords);
        } catch (e) {
          console.error('Failed to parse coordinates:', e);
        }
      }

      const id = rowData['Procurement ID'] || rowData.id || 'coordinates';
      const filename = `${id}_polygon.json`;

      const blob = new Blob([JSON.stringify(coords, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading JSON:', error);
    }
  };

  return (
    <>
      <div className="">
        {/* Action Buttons Row */}
        <Row className="d-flex justify-content-end mb-2">
          <Col className="text-end">
            {buttons
              ?.filter(button => button.enabled)
              .map((button, index) => (
                <Button
                  key={index}
                  variant="primary"
                  onClick={() => navigate(`/${tenant}${button.redirectPath}`)}
                  className={index > 0 ? 'ms-2' : ''}
                >
                  {button.buttonName}
                </Button>
              ))}
          </Col>
        </Row>

        {/* Main Card */}
        <Card>
          {/* Card Header */}
          {showHeader && (
            <FalconCardHeader
              title={title}
              titleTag="h5"
              light={true}
              endEl={
                <div className="d-flex justify-content-end align-items-center my-auto gap-2">
                  {exportKey && <ExportButton exportKey={exportKey} />}
                  <CardDropdown
                    onExport={handleDownload}
                    exportKey={exportKey}
                  />
                </div>
              }
            />
          )}

          <CardBody>
            {/* Mobile Filter Toggle */}
            <div className="d-md-none mb-3">
              <button
                type="button"
                className="btn btn-outline-primary w-100"
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
            </div>

            {/* Filters Component */}
            <MobileFilters
              showFilters={showFilters}
              globalSearch={globalSearch}
              dateFilter={dateFilter}
              dropdownFilter={dropdownFilter}
              dropdownData={dropdownData}
              dropdownOptions={dropdownOptions}
              selectedValues={selectedValues}
              setSelectedValues={setSelectedValues}
              globalfilters={safeGlobalFilters} // Pass actual global filters, not overridden ones
              handleGlobalSearch={handleGlobalSearch}
              handleDateChange={handleDateChange}
              clearAllDates={clearAllDates}
              customStyles={customStyles}
            />

            {/* Table Content or No Data Message */}
            {rowCount > 0 || !isGlobalSearchActive ? (
              <>
                <TableResponsiveCard
                  page={filteredTableData || flattenedData}
                  cardTittles={cardTittles}
                  setIsModalOpen={setShowButtonModal}
                  setSelectedRowData={setSelectedModalData}
                  handleAction={handleMobileAction}
                  renderCollapsibleContent={renderCollapsibleContent}
                  prepareRow={row => row}
                  expandedRow={null}
                />

                {/* Footer */}
                {showFooter && (
                  <div className="mt-4">
                    <ServerSideTableFooter
                      rowCount={rowCount}
                      pageSize={pageSize}
                      pageIndex={pageIndex}
                      setPageSize={handlePageSizeChange}
                      canPreviousPage={pageIndex > 0}
                      canNextPage={canNextPage}
                      nextPage={() => handlePageChange(pageIndex + 1)}
                      previousPage={() =>
                        handlePageChange(Math.max(0, pageIndex - 1))
                      }
                      rowsPerPageSelection
                      navButtons
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      rowsPerPageOptions={[25, 50, 100, 200]}
                    />
                  </div>
                )}
              </>
            ) : (
              <h1
                className="text-center my-5"
                style={{
                  color: '#6c757d',
                  fontSize: '1.5rem',
                  opacity: 0.7
                }}
              >
                {safeGlobalFilters.search_text !== ''
                  ? `"${safeGlobalFilters.search_text}", not found!`
                  : errorMsg !== '' && errorMsg !== undefined
                  ? errorMsg
                  : 'No data added!'}
              </h1>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Modals */}
      {showViewModal && (
        <ViewComponent
          farmerId={selectedFarmerId}
          rowData={selectedRow}
          onClose={() => setShowViewModal(false)}
        />
      )}

      {(() => {
        if (
          showButtonModal &&
          selectedModalData?.apiPath === '/farmer/sms/log'
        ) {
          return (
            <DeliveryDetailsModal
              show={true}
              onClose={() => setShowButtonModal(false)}
              selectedModalData={selectedModalData}
            />
          );
        } else if (showButtonModal && selectedModalData) {
          return (
            <DynamicModal
              show={true}
              onClose={() => setShowButtonModal(false)}
              selectedModalData={selectedModalData}
            />
          );
        }
        return null;
      })()}
    </>
  );
};

MobileReportTable.propTypes = {
  title: PropTypes.string,
  buttons: PropTypes.array,
  showHeader: PropTypes.bool,
  showFooter: PropTypes.bool,
  exportKey: PropTypes.string,
  handleDownload: PropTypes.func,
  rowCount: PropTypes.number,
  isGlobalSearchActive: PropTypes.bool,
  filteredTableData: PropTypes.array,
  flattenedData: PropTypes.array,
  cardTittles: PropTypes.array,
  handleAction: PropTypes.func.isRequired,
  errorMsg: PropTypes.string,
  globalfilters: PropTypes.object,
  pageSize: PropTypes.number,
  pageIndex: PropTypes.number,
  canNextPage: PropTypes.bool,
  handlePageSizeChange: PropTypes.func,
  handlePageChange: PropTypes.func,
  globalSearch: PropTypes.bool,
  dateFilter: PropTypes.bool,
  dropdownFilter: PropTypes.bool,
  dropdownData: PropTypes.array,
  dropdownOptions: PropTypes.object,
  selectedValues: PropTypes.object,
  setSelectedValues: PropTypes.func,
  handleGlobalSearch: PropTypes.func,
  handleDateChange: PropTypes.func,
  clearAllDates: PropTypes.func,
  customStyles: PropTypes.object
};

export default MobileReportTable;
